/**
 * The route table: one entry per screen. The sidebar (groups = sub-menus), the route guard, the
 * page title and the contextual Help link are all derived from it.
 *
 * To add a screen (W2–W5): write the page in src/app/pages/<module>/, then point its entry's
 * `page` at it (lazy import) instead of `soon(...)` and drop `coming`. Keep `perm` in step with
 * the API's permission names (lib/permissions.ts). See docs/sprints/W1.md.
 */
import {
  Activity, AlarmClock, ArrowDownToLine, ArrowRightLeft, BadgeAlert, Boxes, Building2, ChartNoAxesColumn, CircleHelp, ClipboardCheck, ClipboardList, Cpu,
  FileClock, Gauge, History, House, KeyRound, LifeBuoy, ListChecks, MapPinned, Package, PackageCheck, PackageOpen, PackagePlus, PackageSearch,
  Route as RouteIcon, ScanLine, Search, Settings, ShieldCheck, ShieldHalf, SlidersHorizontal, Store, Tags, Truck, Undo2, Users, UsersRound, Warehouse,
  Webhook, type LucideIcon,
} from 'lucide-react';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { P } from '../lib/permissions';

export type GroupId = 'parcels' | 'inbound' | 'storage' | 'outbound' | 'control' | 'portal' | 'setup' | 'admin' | 'platform';

export interface NavGroup {
  id: GroupId;
  label: string;
  icon: LucideIcon;
}

export const GROUPS: NavGroup[] = [
  { id: 'parcels', label: 'Parcels', icon: Package },
  { id: 'inbound', label: 'Inbound', icon: ArrowDownToLine },
  { id: 'storage', label: 'Storage', icon: Warehouse },
  { id: 'outbound', label: 'Outbound', icon: Truck },
  { id: 'control', label: 'Control', icon: ChartNoAxesColumn },
  { id: 'portal', label: 'Client portal', icon: Store },
  { id: 'setup', label: 'Setup', icon: SlidersHorizontal },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'platform', label: 'Platform', icon: Building2 },
];

/** Sidebar order: top-level links and groups. */
export const NAV_ORDER: (GroupId | string)[] = ['/', 'parcels', 'inbound', 'storage', 'outbound', 'control', 'portal', 'setup', 'admin', 'platform'];

export interface RouteDef {
  path: string;
  title: string;
  icon: LucideIcon;
  /** Sidebar placement: a group id, 'top' for a top-level link, or null (not in the menu). */
  nav: GroupId | 'top' | null;
  /** Any of these permissions opens the screen (empty = any signed-in user). */
  perm: string[];
  /** Help slug for the page header link. */
  help?: string;
  /** Match only this exact path for the active menu item (lists with sub-pages below them). */
  end?: boolean;
  /** A module a later sprint builds: "Soon" pill in the menu, placeholder page. */
  coming?: string;
  page: ComponentType | LazyExoticComponent<ComponentType>;
}


const Home = lazy(() => import('./pages/home/HomePage'));
const HelpIndex = lazy(() => import('./help/HelpIndex'));
// W1 parcels
const ParcelsPage = lazy(() => import('./pages/parcels/ParcelsPage'));
const ParcelPage = lazy(() => import('./pages/parcels/ParcelPage'));
const ScanPage = lazy(() => import('./pages/parcels/ScanPage'));
// W5 control + portal
const CountsPage = lazy(() => import('./pages/control/CountsPage'));
const CountPage = lazy(() => import('./pages/control/CountPage'));
const AgingPage = lazy(() => import('./pages/control/AgingPage'));
const KpiPage = lazy(() => import('./pages/control/KpiPage'));
const EventLogPage = lazy(() => import('./pages/control/EventLogPage'));
const AccessLogPage = lazy(() => import('./pages/control/AccessLogPage'));
const ReaderGatewayPage = lazy(() => import('./pages/control/ReaderGatewayPage'));
const PortalPage = lazy(() => import('./pages/portal/PortalPage'));
const WebhooksPage = lazy(() => import('./pages/portal/WebhooksPage'));
const PortalKeysPage = lazy(() => import('./pages/portal/PortalKeysPage'));
// W3 outbound
const SortPage = lazy(() => import('./pages/outbound/SortPage'));
const ContainersPage = lazy(() => import('./pages/outbound/ContainersPage'));
const ContainerPage = lazy(() => import('./pages/outbound/ContainerPage'));
const ManifestsPage = lazy(() => import('./pages/outbound/ManifestsPage'));
const ManifestPage = lazy(() => import('./pages/outbound/ManifestPage'));
const ReturnsPage = lazy(() => import('./pages/outbound/ReturnsPage'));
// W2 inbound + storage
const PreAdvicePage = lazy(() => import('./pages/inbound/PreAdvicePage'));
const PreAdviceDetailPage = lazy(() => import('./pages/inbound/PreAdviceDetailPage'));
const ReceivePage = lazy(() => import('./pages/inbound/ReceivePage'));
const PutawayPage = lazy(() => import('./pages/inbound/PutawayPage'));
const MovesPage = lazy(() => import('./pages/storage/MovesPage'));
const FindPage = lazy(() => import('./pages/storage/FindPage'));
const HoldsPage = lazy(() => import('./pages/storage/HoldsPage'));
const ExceptionsPage = lazy(() => import('./pages/storage/ExceptionsPage'));
const ExceptionDetailPage = lazy(() => import('./pages/storage/ExceptionDetailPage'));
// W1 setup
const SitesPage = lazy(() => import('./pages/setup/SitesPage'));
const LocationsPage = lazy(() => import('./pages/setup/LocationsPage'));
const ClientsPage = lazy(() => import('./pages/setup/ClientsPage'));
const RoutesPage = lazy(() => import('./pages/setup/RoutesPage'));
const ReasonsPage = lazy(() => import('./pages/setup/ReasonsPage'));
const DevicesPage = lazy(() => import('./pages/setup/DevicesPage'));
const ThresholdsPage = lazy(() => import('./pages/setup/ThresholdsPage'));
// Platform pieces from the base
const NotificationsPage = lazy(() => import('./pages/records/NotificationsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const GroupsPage = lazy(() => import('./pages/admin/GroupsPage'));
const RolesPage = lazy(() => import('./pages/admin/RolesPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const ActivityPage = lazy(() => import('./pages/admin/ActivityPage'));
const ApiKeysPage = lazy(() => import('./pages/admin/ApiKeysPage'));
const SupportAccessPage = lazy(() => import('./pages/admin/SupportAccessPage'));
const OrgsPage = lazy(() => import('./pages/platform/OrganisationsPage'));
const SupportSessionsPage = lazy(() => import('./pages/platform/SupportSessionsPage'));

const FLOOR = [P.parcelsView];

export const ROUTES: RouteDef[] = [
  { path: '/', title: 'Home', icon: House, nav: 'top', perm: [], help: 'getting-started', page: Home },

  // Parcels (W1): find by any ID, timeline, scan-and-find
  { path: '/parcels', title: 'Find parcels', icon: PackageSearch, nav: 'parcels', perm: FLOOR, help: 'parcels', end: true, page: ParcelsPage },
  { path: '/parcels/scan', title: 'Scan and find', icon: ScanLine, nav: 'parcels', perm: FLOOR, help: 'scanning', page: ScanPage },
  { path: '/parcels/:id', title: 'Parcel', icon: Package, nav: null, perm: FLOOR, help: 'parcels', page: ParcelPage },

  // Inbound (W2)
  { path: '/inbound/pre-advice', title: 'Pre-advice', icon: ClipboardList, nav: 'inbound', perm: [P.preadvice, P.receive, P.portalRequest], help: 'inbound', end: true, page: PreAdvicePage },
  { path: '/inbound/pre-advice/:id', title: 'Pre-advice', icon: ClipboardList, nav: null, perm: [P.preadvice, P.receive, P.portalView], help: 'inbound', page: PreAdviceDetailPage },
  { path: '/inbound/receive', title: 'Receive', icon: PackagePlus, nav: 'inbound', perm: [P.receive], help: 'inbound', page: ReceivePage },
  { path: '/inbound/putaway', title: 'Put away', icon: PackageCheck, nav: 'inbound', perm: [P.putaway], help: 'storage', page: PutawayPage },

  // Storage (W2)
  { path: '/storage/moves', title: 'Moves', icon: ArrowRightLeft, nav: 'storage', perm: [P.putaway], help: 'storage', page: MovesPage },
  { path: '/storage/find', title: 'Find', icon: Search, nav: 'storage', perm: FLOOR, help: 'storage', page: FindPage },
  { path: '/storage/holds', title: 'Holds', icon: AlarmClock, nav: 'storage', perm: [P.holdsPlace, P.holdsRelease], help: 'exceptions', page: HoldsPage },
  { path: '/storage/exceptions', title: 'Exceptions', icon: BadgeAlert, nav: 'storage', perm: [P.exceptionsOpen, P.exceptionsWork, P.exceptionsClose], help: 'exceptions', end: true, page: ExceptionsPage },
  { path: '/storage/exceptions/:id', title: 'Exception', icon: BadgeAlert, nav: null, perm: [P.exceptionsOpen, P.exceptionsWork, P.exceptionsClose, P.portalView], help: 'exceptions', page: ExceptionDetailPage },

  // Outbound (W3)
  { path: '/outbound/sort', title: 'Sort to route', icon: RouteIcon, nav: 'outbound', perm: [P.sort], help: 'outbound-sort', page: SortPage },
  { path: '/outbound/containers', title: 'Bags and cages', icon: Boxes, nav: 'outbound', perm: [P.containers, P.sort, P.manifests, P.load], help: 'outbound-containers', end: true, page: ContainersPage },
  { path: '/outbound/containers/:id', title: 'Container', icon: Boxes, nav: null, perm: [P.containers, P.sort, P.manifests, P.load], help: 'outbound-containers', page: ContainerPage },
  { path: '/outbound/manifests', title: 'Manifests', icon: Truck, nav: 'outbound', perm: [P.manifests, P.load, P.handover, P.rts], help: 'outbound-manifests', end: true, page: ManifestsPage },
  { path: '/outbound/manifests/:id', title: 'Manifest', icon: Truck, nav: null, perm: [P.manifests, P.load, P.handover, P.rts], help: 'outbound-manifests', page: ManifestPage },
  { path: '/outbound/rts', title: 'Returns (RTS)', icon: Undo2, nav: 'outbound', perm: [P.rts, P.deliveryReport], help: 'outbound-returns', page: ReturnsPage },

  // Control (W5)
  { path: '/control/counts', title: 'Cycle counts', icon: ClipboardCheck, nav: 'control', perm: [P.countsRun, P.countsManage, P.countsApprove], help: 'control-counts', end: true, page: CountsPage },
  { path: '/control/counts/:id', title: 'Cycle count', icon: ClipboardCheck, nav: null, perm: [P.countsRun, P.countsManage, P.countsApprove], help: 'control-counts', page: CountPage },
  { path: '/control/aging', title: 'Aging', icon: FileClock, nav: 'control', perm: [P.reports, P.portalView], help: 'control-aging', page: AgingPage },
  { path: '/control/kpis', title: 'KPI dashboard', icon: Gauge, nav: 'control', perm: [P.reports], help: 'control-kpis', page: KpiPage },
  { path: '/control/events', title: 'Event log', icon: History, nav: 'control', perm: [P.audit], help: 'control-audit', page: EventLogPage },
  { path: '/control/access-log', title: 'Access log', icon: ShieldCheck, nav: 'control', perm: [P.audit, P.privacy], help: 'control-privacy', page: AccessLogPage },

  // Client portal (W5)
  { path: '/portal', title: 'My parcels', icon: PackageOpen, nav: 'portal', perm: [P.portalView], help: 'portal', end: true, page: PortalPage },
  { path: '/portal/api-keys', title: 'API keys', icon: KeyRound, nav: 'portal', perm: [P.portalAdmin], help: 'portal', page: PortalKeysPage },
  { path: '/portal/webhooks', title: 'Webhooks', icon: Webhook, nav: 'portal', perm: [P.portalAdmin, P.webhooks], help: 'portal', page: WebhooksPage },

  // Setup (W1)
  { path: '/setup/sites', title: 'Sites', icon: Warehouse, nav: 'setup', perm: [P.sites, P.locations], help: 'sites-locations', page: SitesPage },
  { path: '/setup/locations', title: 'Locations', icon: MapPinned, nav: 'setup', perm: [P.locations, P.labelsPrint], help: 'sites-locations', page: LocationsPage },
  { path: '/setup/clients', title: 'Clients', icon: Store, nav: 'setup', perm: [P.clients], help: 'clients-routes', page: ClientsPage },
  { path: '/setup/routes', title: 'Routes', icon: RouteIcon, nav: 'setup', perm: [P.routes], help: 'clients-routes', page: RoutesPage },
  { path: '/setup/reasons', title: 'Reason codes', icon: ListChecks, nav: 'setup', perm: [P.reasons], help: 'reasons-devices', page: ReasonsPage },
  { path: '/setup/devices', title: 'Devices', icon: Cpu, nav: 'setup', perm: [P.devices], help: 'reasons-devices', page: DevicesPage },
  { path: '/setup/reader-gateway', title: 'Fixed reader', icon: Cpu, nav: 'setup', perm: [P.devices], help: 'reasons-devices', page: ReaderGatewayPage },
  { path: '/setup/thresholds', title: 'Thresholds', icon: SlidersHorizontal, nav: 'setup', perm: [P.settings], help: 'reasons-devices', page: ThresholdsPage },

  // Admin
  { path: '/admin/users', title: 'Users', icon: Users, nav: 'admin', perm: [P.users], help: 'admin', page: UsersPage },
  { path: '/admin/groups', title: 'Teams', icon: UsersRound, nav: 'admin', perm: [P.groups], help: 'admin', page: GroupsPage },
  { path: '/admin/roles', title: 'Roles', icon: KeyRound, nav: 'admin', perm: [P.roles], help: 'admin', page: RolesPage },
  { path: '/admin/settings', title: 'Settings', icon: Settings, nav: 'admin', perm: [P.settings], help: 'admin', page: SettingsPage },
  { path: '/admin/activity', title: 'Activity log', icon: Activity, nav: 'admin', perm: [P.audit], help: 'admin', page: ActivityPage },
  { path: '/admin/api-keys', title: 'API keys', icon: Tags, nav: 'admin', perm: [P.apiKeys], help: 'admin', page: ApiKeysPage },

  // Platform (ITCSI)
  { path: '/platform/orgs', title: 'Tenants', icon: Building2, nav: 'platform', perm: [P.platformOrgs], help: 'admin', page: OrgsPage },
  { path: '/platform/support', title: 'Support sessions', icon: LifeBuoy, nav: 'platform', perm: [P.platformSupport], help: 'admin', page: SupportSessionsPage },

  { path: '/notifications', title: 'Notifications', icon: ListChecks, nav: null, perm: [], page: NotificationsPage },
  { path: '/support', title: 'Support access', icon: ShieldHalf, nav: null, perm: [P.supportApprove], help: 'admin', page: SupportAccessPage },

  { path: '/help', title: 'Help', icon: CircleHelp, nav: 'top', perm: [], page: HelpIndex },
];

/** Routes that sit outside the menu but inside the shell. */
export const EXTRA_ROUTES: { path: string; page: LazyExoticComponent<ComponentType> }[] = [
  { path: '/help/:slug', page: lazy(() => import('./help/HelpArticle')) },
  { path: '/account', page: lazy(() => import('./auth/Account')) },
];

const specificity = (p: string) => p.split('/').reduce((n, s) => n + (s.startsWith(':') ? 1 : 10), 0);

/** The route that best matches `pathname` (static segments beat :params). */
export function routeFor(pathname: string): RouteDef | undefined {
  const parts = pathname.split('/').filter(Boolean);
  const matches = ROUTES.filter((r) => {
    const rp = r.path.split('/').filter(Boolean);
    if (r.path === '/') return pathname === '/';
    if (rp.length !== parts.length) return false;
    return rp.every((s, i) => s.startsWith(':') || s === parts[i]);
  });
  return matches.sort((a, b) => specificity(b.path) - specificity(a.path))[0];
}
